import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

/**
 * underwritRE Design System
 *
 * This file is the single source of truth for all design tokens.
 * Every color, font, radius and component style flows from here.
 *
 * `brand` mirrors the marketing site (underwritre.com) exactly. It is emitted
 * as CSS custom properties (`--ink`, `--accent`, ...) by `globalStyles` below,
 * and the MUI theme and the legacy `colors` aliases are built from the same
 * values, so a change here reaches CSS, `sx` props and MUI components alike.
 *
 * DO NOT hardcode colors or fonts elsewhere in the app.
 * Import these tokens (or use the CSS variables) instead.
 */

// ============================================================================
// BRAND TOKENS (from underwritre.com)
// ============================================================================

export const brand = {
  // Surfaces
  ink: '#05192e',          // main dark navy background
  ink2: '#0a2440',         // slightly lighter navy for cards, panels, sidebars
  paper: '#ffffff',        // white surfaces for dense content (tables, forms)
  paper2: '#f5f6f8',       // light gray alt surface

  // Text
  textDarkBg: '#e8eef8',   // primary text on navy
  mutedDarkBg: '#8fa3bf',  // secondary text on navy
  textLightBg: '#05192e',  // primary text on white
  mutedLightBg: '#5b6270', // secondary text on white

  // Lines and accent (periwinkle, the "RE" in the logo)
  lineDarkBg: 'rgba(154, 186, 246, 0.15)', // borders and dividers on navy
  lineLightBg: '#e4e7ec',                  // borders on white
  accent: '#9abaf6',        // primary accent, primary buttons
  accentNav: '#82a6d7',     // inactive nav links
  accentHover: '#c3d5fa',   // hover for accent buttons

  // App-only derivatives of the accent (not on the marketing site, needed
  // for app states the site doesn't have).
  accentTint: '#ebf1fd',                    // selected rows / highlights on white
  accentOnLight: '#2f5fc4',                 // accent graphics (loaders) on white; periwinkle is too faint there
  accentWashDarkBg: 'rgba(154, 186, 246, 0.12)', // active nav item on navy
  hoverDarkBg: 'rgba(154, 186, 246, 0.06)',      // hover on navy

  radius: 6,
  fontSans: "'Manrope Variable', system-ui, -apple-system, sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, Consolas, monospace",
} as const;

/** CSS custom-property references, for use in `sx` or inline styles. */
export const cssVar = {
  ink: 'var(--ink)',
  ink2: 'var(--ink-2)',
  paper: 'var(--paper)',
  paper2: 'var(--paper-2)',
  textDarkBg: 'var(--text-dark-bg)',
  mutedDarkBg: 'var(--muted-dark-bg)',
  textLightBg: 'var(--text-light-bg)',
  mutedLightBg: 'var(--muted-light-bg)',
  lineDarkBg: 'var(--line-dark-bg)',
  lineLightBg: 'var(--line-light-bg)',
  accent: 'var(--accent)',
  accentNav: 'var(--accent-nav)',
  accentHover: 'var(--accent-hover)',
  radius: 'var(--radius)',
  fontSans: 'var(--font-sans)',
  fontMono: 'var(--font-mono)',
} as const;

/** Standard transition for color, background and border changes. */
export const transition =
  'color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease';

// ============================================================================
// COLOR PALETTE (legacy aliases, used by existing screens)
// ============================================================================

/**
 * These names predate the rebrand and are imported across the app. Their values
 * now point at the brand tokens above: "navy" is the new ink, and "blue" (the
 * interactive color used inside white content panels) is ink too, because the
 * periwinkle accent is too light to read as text or a thin border on white.
 */
export const colors = {
  navy: brand.ink,            // Nav, page headers, structural chrome
  blue: brand.ink,            // Interactive elements and focus states on white
  blueDark: brand.ink2,       // Hover state for "blue" elements
  blueLight: brand.accent,    // Secondary interactive elements, progress
  blueTint: brand.accentTint, // Selected rows, active card backgrounds

  // Neutrals for white surfaces
  grey: {
    900: brand.textLightBg,   // Primary text
    700: '#3a4250',           // Secondary text (labels, metadata)
    600: brand.mutedLightBg,  // Tertiary text (captions, helper text)
    400: '#9ca3af',           // Disabled text, placeholders
    300: brand.lineLightBg,   // Borders, dividers
    100: brand.paper2,        // Alternating rows, light surface
    50: '#f3f4f6',            // Calculated cell backgrounds
  },

  // Semantic colors (unchanged; used on white panels)
  error: '#DC2626',
  errorBg: '#FEF2F2',
  success: '#059669',
  successBg: '#D1FAE5',

  white: '#FFFFFF',
  black: '#000000',
} as const;

/**
 * Input State Tokens
 *
 * Make editable vs calculated cells visually distinct.
 */
export const inputStates = {
  editable: {
    background: brand.paper,
    border: brand.lineLightBg,
    borderFocus: brand.ink,
    text: brand.textLightBg,
  },
  calculated: {
    background: colors.grey[50],
    border: 'none',
    text: colors.grey[600],
  },
  error: {
    background: colors.errorBg,
    border: colors.error,
    text: colors.error,
  },
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

/**
 * Manrope (variable) for all text; IBM Plex Mono only for numbers (see `.num`).
 * Both are self-hosted via @fontsource and imported in index.tsx.
 */
export const typography = {
  fontFamily: brand.fontSans,
  fontFamilyMono: brand.fontMono,

  fontSize: {
    xs: '0.75rem',      // 12px - captions, fine print
    sm: '0.875rem',     // 14px - table cells, secondary text
    base: '0.9375rem',  // 15px - body text (dense app screens)
    lg: '1.125rem',     // 18px - emphasized body
    xl: '1.25rem',      // 20px - section headers
    '2xl': '1.5rem',    // 24px - page headers
    '3xl': '1.875rem',  // 30px - major headers
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    heading: 1.1,
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.6,
  },

  headingLetterSpacing: '-0.02em',
} as const;

/** Mono numeric style. Spread into `sx` for figures, metrics and table values. */
export const numStyle = {
  fontFamily: brand.fontMono,
  fontVariantNumeric: 'tabular-nums',
} as const;

// ============================================================================
// SHELL (navy chrome: sidebar, top bars, wizard rail)
// ============================================================================

/** Height of the sidebar logo row and every page's top bar. */
export const TOP_BAR_HEIGHT_PX = 76;

/**
 * Sticky navy top bar. Solid rather than the site's translucent blur: in the
 * app it sits over light content, which washed the translucent navy out to grey.
 */
export const topBarSx = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: brand.ink,
  borderBottom: `1px solid ${brand.lineDarkBg}`,
  color: brand.textDarkBg,
} as const;

/** Sidebar / rail nav item on navy: accent-nav text, white on hover and when active. */
export const navItemSx = (active: boolean) => ({
  borderRadius: `${brand.radius}px`,
  py: 1.25,
  color: active ? colors.white : brand.accentNav,
  backgroundColor: active ? brand.accentWashDarkBg : 'transparent',
  transition,
  '& .MuiListItemIcon-root': { color: 'inherit' },
  '& .MuiListItemText-primary': {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: 'inherit',
  },
  '&:hover': {
    color: colors.white,
    backgroundColor: active ? brand.accentWashDarkBg : brand.hoverDarkBg,
  },
});

/** Secondary button on navy: transparent, hairline border, accent border on hover. */
export const secondaryOnDarkButtonSx = {
  color: colors.white,
  backgroundColor: 'transparent',
  border: `1px solid ${brand.lineDarkBg}`,
  '&:hover': {
    backgroundColor: 'transparent',
    borderColor: brand.accent,
  },
} as const;

// ============================================================================
// SPACING
// ============================================================================

/**
 * Base unit: 8px. MUI's spacing function multiplies these: spacing(1) = 8px
 */
export const spacing = {
  xs: 0.5,   // 4px
  sm: 1,     // 8px
  md: 2,     // 16px
  lg: 3,     // 24px
  xl: 4,     // 32px
  '2xl': 6,  // 48px
  '3xl': 8,  // 64px
} as const;

// ============================================================================
// GLOBAL CSS (tokens as CSS variables + utility classes)
// ============================================================================

/**
 * Rendered once via <GlobalStyles styles={globalStyles} /> in index.tsx.
 */
export const globalStyles = {
  ':root': {
    '--ink': brand.ink,
    '--ink-2': brand.ink2,
    '--paper': brand.paper,
    '--paper-2': brand.paper2,
    '--text-dark-bg': brand.textDarkBg,
    '--muted-dark-bg': brand.mutedDarkBg,
    '--text-light-bg': brand.textLightBg,
    '--muted-light-bg': brand.mutedLightBg,
    '--line-dark-bg': brand.lineDarkBg,
    '--line-light-bg': brand.lineLightBg,
    '--accent': brand.accent,
    '--accent-nav': brand.accentNav,
    '--accent-hover': brand.accentHover,
    '--radius': `${brand.radius}px`,
    '--font-sans': brand.fontSans,
    '--font-mono': brand.fontMono,
  },
  body: {
    fontFamily: 'var(--font-sans)',
    fontSize: typography.fontSize.base,
    lineHeight: typography.lineHeight.relaxed,
    backgroundColor: 'var(--ink)',
    color: 'var(--text-dark-bg)',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  // Numbers: financial figures, table values, metrics.
  '.num': {
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
  },
  '.wordmark': {
    fontFamily: 'var(--font-sans)',
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1,
    color: '#fff',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  '.wordmark span': {
    color: 'var(--accent)',
  },
  ':focus-visible': {
    outline: '2px solid var(--accent)',
    outlineOffset: 3,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '*, *::before, *::after': {
      transitionDuration: '0.01ms !important',
      animationDuration: '0.01ms !important',
      animationIterationCount: '1 !important',
      scrollBehavior: 'auto !important',
    },
  },
} as const;

// ============================================================================
// MUI THEME
// ============================================================================

const headingBase = {
  fontWeight: typography.fontWeight.semibold,
  lineHeight: typography.lineHeight.heading,
  letterSpacing: typography.headingLetterSpacing,
};

/**
 * Applied globally via ThemeProvider in App.tsx.
 *
 * Content screens sit on white panels, so the palette's text colors are the
 * "on white" tokens. Navy chrome (sidebar, top bar, wizard rail) styles its
 * text explicitly with the "on navy" tokens.
 */
export const theme = createTheme({
  palette: {
    primary: {
      main: brand.accent,
      light: brand.accentHover,
      dark: brand.accentNav,
      contrastText: brand.ink,
    },
    secondary: {
      main: brand.ink,
      light: brand.ink2,
      contrastText: colors.white,
    },
    error: {
      main: colors.error,
      light: colors.errorBg,
    },
    success: {
      main: colors.success,
      light: colors.successBg,
    },
    text: {
      primary: brand.textLightBg,
      secondary: brand.mutedLightBg,
      disabled: colors.grey[400],
    },
    divider: brand.lineLightBg,
    // The navy page background comes from globalStyles (body) and the shell.
    // `default` stays white: MUI X DataGrid paints its rows with it.
    background: {
      default: brand.paper,
      paper: brand.paper,
    },
    grey: colors.grey,
  },

  typography: {
    fontFamily: typography.fontFamily,
    fontSize: 14,

    h1: { ...headingBase, fontSize: typography.fontSize['3xl'] },
    h2: { ...headingBase, fontSize: typography.fontSize['2xl'] },
    h3: { ...headingBase, fontSize: typography.fontSize.xl },
    h4: { ...headingBase, fontSize: typography.fontSize.lg },
    h5: { ...headingBase, fontSize: typography.fontSize.base },
    h6: { ...headingBase, fontSize: typography.fontSize.sm },
    subtitle1: { fontWeight: typography.fontWeight.semibold },
    subtitle2: { fontWeight: typography.fontWeight.semibold },
    body1: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.relaxed,
    },
    body2: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    caption: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    overline: {
      textTransform: 'none',
      letterSpacing: 0,
      fontWeight: typography.fontWeight.semibold,
    },
    button: {
      fontSize: '1rem',
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'none',
      letterSpacing: 0,
      lineHeight: typography.lineHeight.normal,
    },
  },

  spacing: 8,

  shape: {
    borderRadius: brand.radius,
  },

  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: brand.radius,
          fontWeight: typography.fontWeight.semibold,
          textTransform: 'none',
          letterSpacing: 0,
          boxShadow: 'none',
          transition,
          '&:hover': { boxShadow: 'none' },
        },
        sizeLarge: { padding: '13px 24px', fontSize: '1rem' },
        sizeMedium: { padding: '10px 20px', fontSize: '0.9375rem' },
        sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
        containedPrimary: {
          backgroundColor: brand.accent,
          color: brand.ink,
          '&:hover': { backgroundColor: brand.accentHover },
        },
        containedSecondary: {
          backgroundColor: brand.ink,
          color: colors.white,
          '&:hover': { backgroundColor: brand.ink2 },
        },
        // Outlined / text buttons live on white panels: navy text, light border,
        // accent border on hover (the light-surface twin of the site's
        // secondary button).
        outlinedPrimary: {
          color: brand.ink,
          borderColor: brand.lineLightBg,
          '&:hover': {
            borderColor: brand.accent,
            backgroundColor: 'transparent',
          },
        },
        textPrimary: {
          color: brand.ink,
          '&:hover': { backgroundColor: brand.accentTint },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { transition },
        colorPrimary: { color: brand.ink },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: brand.ink,
          textDecorationColor: brand.accent,
          transition,
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: { '&.Mui-checked': { color: brand.ink } },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorPrimary: { '&.Mui-checked': { color: brand.ink } },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: brand.ink },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: brand.accent, opacity: 1 },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          letterSpacing: 0,
          fontWeight: typography.fontWeight.medium,
          '&.Mui-selected': { color: brand.ink, fontWeight: typography.fontWeight.semibold },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: brand.ink },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: brand.radius,
          boxShadow: 'none',
          border: `1px solid ${brand.lineLightBg}`,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: brand.radius,
        },
        elevation1: { boxShadow: '0 1px 2px rgba(5,25,46,0.06)' },
        elevation2: { boxShadow: '0 2px 8px rgba(5,25,46,0.08)' },
        elevation3: { boxShadow: '0 4px 12px rgba(5,25,46,0.10)' },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { ...headingBase, fontSize: typography.fontSize.xl },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: brand.radius,
          backgroundColor: inputStates.editable.background,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: inputStates.editable.border,
            transition,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.mutedLightBg,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: inputStates.editable.borderFocus,
            borderWidth: 2,
          },
        },
        // Numeric inputs (type="number" or inputMode decimal/numeric) use mono.
        input: {
          '&[type="number"], &[inputmode="decimal"], &[inputmode="numeric"]': numStyle,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: brand.ink },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontFamily: brand.fontSans,
          borderColor: brand.lineLightBg,
          fontVariantNumeric: 'tabular-nums',
          '--DataGrid-rowBorderColor': brand.lineLightBg,
          // Right-aligned cells are the numeric columns.
          '& .MuiDataGrid-cell--textRight': numStyle,
          '& .MuiDataGrid-cell--textRight input': numStyle,
        },
        columnHeaderTitle: {
          fontWeight: typography.fontWeight.semibold,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: brand.lineLightBg },
        head: { fontWeight: typography.fontWeight.semibold },
        // Right-aligned table cells are numeric.
        alignRight: numStyle,
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brand.ink2,
          color: brand.textDarkBg,
          border: `1px solid ${brand.lineDarkBg}`,
          fontSize: typography.fontSize.xs,
        },
      },
    },
  },
});

// ============================================================================
// HELPER UTILITIES
// ============================================================================

export const shadows = {
  xs: '0 1px 2px rgba(5,25,46,0.05)',
  sm: '0 1px 3px rgba(5,25,46,0.08)',
  md: '0 4px 6px rgba(5,25,46,0.08)',
  lg: '0 10px 15px rgba(5,25,46,0.08)',
  xl: '0 20px 25px rgba(5,25,46,0.08)',
} as const;

export const focusRing = {
  outline: `2px solid ${brand.accent}`,
  outlineOffset: '3px',
} as const;
